import { Button, Col, Form, message, Row, Switch } from "antd";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AttributeBox from "../../../commons/components/AttributeBox/AttributeBox";
import BorderGradientButton from "../../../commons/components/BorderGradientButton/BorderGradientButton";
import { CategorySelect } from "../../../commons/components/CategorySelect/CategorySelect";
import CustomDropImageInput from "../../../commons/components/CustomDropImageInput/CustomDropImageInput";
import CustomInput from "../../../commons/components/CustomInput/CustomInput";
import { CustomSelect } from "../../../commons/components/CustomSelect/CustomSelect";
import FormBox from "../../../commons/components/FormBox/FormBox";
import FormHeader from "../../../commons/components/FormHeader/FormHeader";
import GradientText from "../../../commons/components/GradientText/GradientText";
import TextArea from "../../../commons/components/TextArea/TextArea";
import { MediaDirectoryEnum, StoreExample } from "../../../commons/constants";
import { LIST_MENU_OF_STORE, MARKUP_FEES, UPDATE_PRODUCT_APPROVAL_URL } from "../../../commons/constants/api-urls";
import { MAIN_TAG_OPTIONS, ProductStatusEnum, TYPE_OF_ATTRIBUTE } from "../../../commons/constants/product";
import { routerNavigation } from "../../../commons/constants/routers";
import defaultAxios from "../../../commons/utils/axios";
import {
  decimalOnlyInput,
  filterECharacterInputDecimal,
  filterECharacterInputNumber,
  integerOnlyInput,
} from "../../../commons/utils/functions/integerOnly";
import {
  createNewAttribute,
  getBodyFromProductValues,
  getValuesFromProductDetail,
} from "../../../commons/utils/functions/productMapping";
import removeExtraSpace from "../../../commons/utils/functions/removeExtraSpace";
import { uploadImageToServer } from "../../../commons/utils/functions/uploadImageToServer";
import { isPositiveNumber, isPositiveNumberOrZero } from "../../../commons/utils/functions/validation";
import { AppContext } from "../../../contexts/AppContext";
import PreviewProduct from "../../PreviewProduct/PreviewProduct";
import styles from "./CreateUpdateProductForm.module.scss";

type Props = {
  product?: Product | null;
  approvalId?: number;
  approvalType?: string;
};

export enum ApprovalStatus {
  PENDING = 0,
  APPROVE = 1,
  REJECT = 2,
  DELETE = 3,
}

export enum ProductActionType {
  CREATE = "Create",
  UPDATE = "Update",
  PUBLISH = "Publish",
}

const CreateUpdateProductApprovalForm = ({ product, approvalId, approvalType }: Props) => {
  const { user, currentStore, configAttributes, currentStoreDetail } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  // get current url to check if user is in create or update page
  const [form] = Form.useForm<CreateUpdateProductValues>();
  const [isFormLoad, setIsFormLoad] = useState<boolean>(false);
  const [preview, setPreview] = useState<boolean>(false);
  const [markup, setMarkup] = useState(0);
  const attr = Form.useWatch("attributes", form);
  const productType = Form.useWatch("type", form);
  const [loading, setLoading] = useState<"draft" | "save" | false>(false);
  const [productPreview, setProductPreview] = useState<CreateUpdateProductBody | null>(null);
  const [isSellingMode, setIsSellingMode] = useState(false);
  const [listGroupMenu, setListGroupMenu] = useState<{ value: string; label: string }[]>([]);

  const isInApprovePage = useMemo(() => {
    return location.pathname.includes("approval");
  }, []);

  async function handleGetListGroupMenu() {
    try {
      const { data } = await defaultAxios.get<GroupMenuList[]>(LIST_MENU_OF_STORE(currentStore.id));
      const list = data.map(item => ({ label: item.name, value: item.id }));
      setListGroupMenu(list);
    } catch (error) {
      message.error("Can't get list menu");
      console.log(error);
    }
  }

  async function handleGetMarkup() {
    try {
      const res = await defaultAxios.get(MARKUP_FEES(currentStore?.id));
      console.log(res);
      setMarkup(res.data.mark_up);
    } catch (error) {
      message.error("Get markup failed");
    }
  }

  useEffect(() => {
    if (currentStore.id !== StoreExample.id) {
      handleGetMarkup();
      handleGetListGroupMenu();
    }
  }, [currentStore]);

  useEffect(() => {
    const price = form.getFieldValue("price");
    const costPrice = form.getFieldValue("costPrice");
    const attributes: CreateUpdateProductValues["attributes"] = form.getFieldValue("attributes");
    const requiredAttributes = attributes.filter(attribute =>
      attribute.type_of_attribute.includes(TYPE_OF_ATTRIBUTE.is_required)
    );
    if (requiredAttributes.length > 0) {
      let price = 0;
      requiredAttributes.forEach(attribute => {
        let minVariantPrice = Number.MAX_SAFE_INTEGER;
        attribute.variants.forEach((variant: ProductVariationValues) => {
          if (Number(variant.price) < minVariantPrice) {
            minVariantPrice = Number(variant.price);
          }
        });
        price += minVariantPrice;
      });
      form.setFieldValue("price", price);
    } else {
      form.setFieldValue("price", price);
      form.setFieldValue("costPrice", costPrice);
    }
  }, [attr, form]);

  useEffect(() => {
    if (isFormLoad || !product || !markup) return;
    setIsSellingMode(product.isSellingMode);
    form.setFieldsValue(getValuesFromProductDetail(markup, product, currentStore?.id, configAttributes));
    setIsFormLoad(true);
  }, [product, currentStore?.id, form, configAttributes, markup]);

  const renderCateList = () => {
    const cateList = [
      ...currentStoreDetail.categoriesLevel1,
      ...currentStoreDetail.categoriesLevel2,
      ...currentStoreDetail.categoriesLevel3,
    ];
    return cateList.map((item, idx) => {
      if (!item) return null;
      if (idx === cateList.length - 1) return <div key={idx}>{item.name}.</div>;
      return <div key={idx}>{item.name},</div>;
    });
  };

  const onFinish = async (values: CreateUpdateProductValues) => {
    if (approvalType === ProductActionType.UPDATE || approvalType === ProductActionType.CREATE) {
      setLoading("save");
      try {
        if (!currentStore) {
          message.error(`Please select store`);
          return;
        }
        // const { images, videos } = values;
        const { images } = values;
        const imageUrls = images.filter(image => typeof image === "string") as string[];
        // const videoUrls = videos.filter(video => typeof video === "string") as string[];
        const newImages = images.filter(image => typeof image !== "string");
        // const newVideos = videos.filter(video => typeof video !== "string");
        if (newImages) {
          await uploadImageToServer(newImages, MediaDirectoryEnum.PRODUCT).then(res => {
            if (Array.isArray(res)) {
              res.forEach(item => imageUrls.push(item));
            } else {
              imageUrls.push(res);
            }
          });
        }

        // if (newVideos) {
        //   await uploadImageToServer(newVideos, MediaDirectoryEnum.PRODUCT).then(res => {
        //     if (Array.isArray(res)) {
        //       res.forEach(item => videoUrls.push(item));
        //     } else {
        //       videoUrls.push(res);
        //     }
        //   });
        // }

        const body = {
          ...getBodyFromProductValues(isSellingMode, values, currentStore.id),
          images: imageUrls,
          thumbnail: imageUrls[0],
          // videos: videoUrls,
        };
        const realBody = {
          ...body,
          status: ProductStatusEnum.ACTIVE,
        };

        await defaultAxios.put(UPDATE_PRODUCT_APPROVAL_URL(approvalId ? approvalId : 0), {
          approval_status: ApprovalStatus.PENDING,
          update_product_dto: realBody,
        });

        message.success(`Update request  successfully`);
        navigate(routerNavigation.STORES.PRODUCT_MANAGEMENT(currentStore.id.toString()));
      } catch (error: any) {
        message.error(`Update request failed`);
      }
      setLoading(false);
    }
  };
  const getActionBtn = useCallback(() => {
    if (isInApprovePage)
      return (
        <>
          <Button type="text" className={styles.cancel} onClick={() => navigate(-1)}>
            Back
          </Button>
        </>
      );
    return (
      <>
        {" "}
        <Button type="text" className={styles.cancel} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          type="text"
          className={styles.preview}
          onClick={() => {
            setProductPreview(getBodyFromProductValues(isSellingMode, form.getFieldsValue()));
            setPreview(true);
          }}
        >
          <GradientText text="Preview" />
        </Button>
        {approvalType !== ProductActionType.PUBLISH && (
          <Button
            type="primary"
            htmlType="submit"
            className={styles.save}
            disabled={!!loading}
            loading={loading === "save"}
          >
            {loading ? "Saving" : "Save"}
          </Button>
        )}
      </>
    );
  }, [isInApprovePage]);
  const onFinishFailed = (errorInfo: any) => {
    const namePath = errorInfo.errorFields[0].name;
    form.getFieldInstance(namePath)?.focus();
  };

  function onChangeSwitch(checked: boolean) {
    setIsSellingMode(checked);
  }
  return (
    <>
      <Form
        form={form}
        name="create-product"
        wrapperCol={{ span: 24 }}
        initialValues={getValuesFromProductDetail(markup, product, user?.merchantIds?.[0])}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        autoComplete="off"
      >
        <FormHeader
          title={
            approvalType === ProductActionType.CREATE
              ? `Request create product: "${product?.name}"`
              : approvalType === ProductActionType.UPDATE
              ? `Request update product: "${product?.name}"`
              : `Request publish product: "${product?.name}"`
          }
          actions={getActionBtn()}
        />

        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>Basic information</p>
            </div>
          }
        >
          <CustomInput
            disabled={isInApprovePage}
            name="name"
            label="Product name"
            placeholder="Enter product name"
            onMouseMove={removeExtraSpace("name", form)}
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            maxLength={120}
          />
          <CustomDropImageInput
            disabled={isInApprovePage}
            name="images"
            label="Product image"
            square
            multiple={10}
            description="Up to 10 images"
            onError={err => form?.setFields([{ name: "images", errors: [err] }])}
            rules={[{ required: true, message: "This field is required" }]}
            isEdit={product?.name ? true : false}
            form={form}
          />
          {/* <VideoInput
            disabled={isInApprovePage}
            name="videos"
            label="Update Product video"
            onError={err => form?.setFields([{ name: "videos", errors: [err] }])}
            isEdit={product?.name ? true : false}
            form={form}
          /> */}
          <Row gutter={24}>
            <Col xs={24} sm={24}>
              <Row>
                <Col span={6}>
                  <div className={styles.fieldTitle}>Product Category*</div>
                </Col>
                <Col span={18}>
                  <div className={styles.fieldContent}>{renderCateList()}</div>
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={6}>
              <CustomInput
                type="number"
                name="shipment_weight"
                label="Product weight"
                placeholder="Enter product weight"
                onBlur={removeExtraSpace("shipment_weight", form)}
                rules={[
                  {
                    required: true,
                    message: "This field is required",
                    whitespace: true,
                  },
                  {
                    validator: isPositiveNumber("shipment_weight"),
                  },
                ]}
                maxLength={120}
                prefix="Gram |"
                onKeyDown={integerOnlyInput}
                onChange={event => filterECharacterInputNumber("shipment_weight", form, event)}
                disabled={isInApprovePage}
              />
            </Col>
            <Col xs={24} sm={6}>
              <CustomSelect
                name="main_tags"
                label="Product Tag"
                options={MAIN_TAG_OPTIONS}
                placeholder="Product Main Tag"
                disabled={isInApprovePage}
              />
            </Col>

            <Col xs={24} sm={6}>
              <CustomSelect
                name="groupMenuId"
                label="Store Menus"
                options={listGroupMenu}
                placeholder="Store Menus"
                disabled={isInApprovePage}
              />
            </Col>
            {/* <Col xs={24} sm={6}>
              <CustomSelect
                name="type"
                label="Product Type"
                options={TYPE_OF_PRODUCT_OPTIONS}
                rules={[{ required: true, message: "This field is required" }]}
                placeholder="Product Type"
              />
            </Col>
            <Col xs={24} sm={24}>
              {productType === TYPE_OF_PRODUCTS.RETAIL && (
                <CustomInput
                  type="text"
                  name="quantity"
                  label="Product quantiy"
                  placeholder="0"
                  rules={[
                    { required: true, message: "This field is required" },
                    { validator: isPositiveNumber("quantity") },
                  ]}
                  onKeyDown={event => {
                    integerOnlyInput(event);
                  }}
                  onChange={event => {
                    filterECharacterInputNumber("quantity", form, event);
                  }}
                />
              )}
            </Col> */}
          </Row>
          <TextArea
            name="description"
            label="Description"
            disabled={isInApprovePage}
            onMouseMove={removeExtraSpace("description", form)}
            placeholder="Tell us something about your product"
            rules={[
              {
                required: true,
                message: "This field is required",
                whitespace: true,
              },
            ]}
            showCount
            maxLength={3000}
          />
        </FormBox>
        <FormBox
          title={
            <div className={styles.formToggleBox}>
              <p>
                Selling information{" "}
                <Switch
                  style={{ marginLeft: "15px" }}
                  checked={isSellingMode}
                  onChange={onChangeSwitch}
                  checkedChildren={"Selling Mode"}
                  unCheckedChildren={"Cost Mode"}
                />
              </p>
              <p>Mark Up: {markup + "%"} </p>
            </div>
          }
        >
          <Form.Item dependencies={["attributes"]}>
            {({ getFieldValue }) => {
              const atrributes: CreateUpdateProductValues["attributes"] = getFieldValue("attributes");
              const isNoVariant = !atrributes?.length;
              const isNoRequiredAttribute = !atrributes.some(item =>
                item.type_of_attribute.includes(TYPE_OF_ATTRIBUTE.is_required)
              );
              const isRequired = isNoVariant || isNoRequiredAttribute;

              return (
                <Row gutter={24}>
                  <Col xs={12} sm={12}>
                    <CustomInput
                      disabled={isInApprovePage}
                      type="text"
                      name="costPrice"
                      label="Product Cost Price (Amount due to Merchant)"
                      placeholder="0.00"
                      readOnly={isSellingMode ? true : false}
                      rules={[
                        { required: true, message: "This field is required" },
                        { validator: isPositiveNumberOrZero("costPrice") },
                      ]}
                    />
                  </Col>
                  <Col xs={12} sm={12}>
                    <CustomInput
                      disabled={isInApprovePage}
                      type="text"
                      name="price"
                      label="Product Selling Price"
                      placeholder="0.00"
                      readOnly={isSellingMode ? false : true}
                      rules={[
                        { required: true, message: "This field is required" },
                        { validator: isPositiveNumberOrZero("price") },
                      ]}
                    />
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
          <Form.List name="attributes">
            {(attributes, { add, remove }: FormListOperationCustom<CreateUpdateProductValues["attributes"][0]>) => {
              return (
                <>
                  {attributes.map(({ key, name, ...restAttribute }) => {
                    return (
                      <AttributeBox
                        markup={markup}
                        key={key}
                        attributeIndex={name}
                        restAttribute={restAttribute}
                        remove={remove}
                        disabled={isInApprovePage}
                        form={form}
                        isSellingMode={isSellingMode}
                      />
                    );
                  })}
                  <div
                    className={styles.addAttribute}
                    onClick={() => {
                      if (isInApprovePage) return;
                      add(createNewAttribute());
                    }}
                  >
                    <BorderGradientButton disabled={isInApprovePage}>
                      <GradientText text="Add more attribute" />
                    </BorderGradientButton>
                  </div>
                </>
              );
            }}
          </Form.List>
        </FormBox>
        <FormBox title="">
          <Row gutter={24}>
            <Col xs={24} sm={24}>
              <CustomInput
                disabled={isInApprovePage}
                name="lead_time"
                label="Lead time"
                placeholder="Lead time"
                suffix="minutes"
                rules={[
                  {
                    validator: isPositiveNumberOrZero("Lead time"),
                  },
                ]}
                onChange={event => filterECharacterInputNumber("lead_time", form, event)}
              />
            </Col>
          </Row>
        </FormBox>
      </Form>
      {productPreview && <PreviewProduct open={preview} onCancel={() => setPreview(false)} product={productPreview} />}
    </>
  );
};

export default CreateUpdateProductApprovalForm;
